/* ONE TUNNE - garage & fitment, client side only.
 *
 * The model lives in localStorage, never in a cookie and never in Liquid: the HTML is the
 * same for everyone, so the page stays cacheable. Every visible string comes from a
 * data-attribute filled in by Liquid `| t`. Without JS, or with storage blocked, nothing
 * is hidden and every label keeps its server text.
 *
 *   <garage-store key> - {slug, name} + `garage:changed` event on document
 *   <fitment-finder> - model chips (aria-pressed) + the CTA label, built here
 *   <garage-filter mode="hide|mark"> - shows/hides/marks descendants with data-fits
 *   <fit-badge> - fits / does not fit / no model chosen
 *
 * GarageStore.get() / .set(slug, name) / .clear() - no global, reached through
 * customElements.get('garage-store'). Keep the file under 10 KB raw (MCP validator).
 */
(function () {
  'use strict';

  const EVENT = 'garage:changed';
  const DEFAULT_KEY = 'ot:garage';
  const HANDLER = '_otGarageHandler';

  /* The tag repeats across blocks: one cached copy, but every tag runs and define() throws. */
  if (window.customElements.get('garage-store')) return;

  function read(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function write(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {}
  }

  function drop(key) {
    try {
      window.localStorage.removeItem(key);
    } catch (error) {}
  }

  function fitsModel(value, slug) {
    if (!value || !slug) return false;
    return (' ' + value + ' ').indexOf(' ' + slug + ' ') > -1;
  }

  function fill(template, count, model) {
    if (!template) return '';
    return template.split('{count}').join(count).split('{model}').join(model);
  }

  function setText(element, value) {
    if (element && value) element.textContent = value;
  }

  /* uk needs one/few/many where en/es/fr/de need one+other: the browser picks it. */
  function pluralForm(count) {
    try {
      return new Intl.PluralRules(document.documentElement.lang || 'en').select(count);
    } catch (error) {
      return count === 1 ? 'one' : 'other';
    }
  }

  let memory = null;

  class GarageStore extends HTMLElement {
    static get() {
      if (memory) return memory;
      const raw = read(GarageStore.key);
      if (!raw) return null;
      let parsed = null;
      try {
        parsed = JSON.parse(raw);
      } catch (error) {
        parsed = null;
      }
      if (!parsed || !parsed.slug) return null;
      memory = { slug: String(parsed.slug), name: String(parsed.name || parsed.slug) };
      return memory;
    }

    static set(slug, name) {
      if (!slug) return GarageStore.clear();
      memory = { slug: String(slug), name: String(name || slug) };
      write(GarageStore.key, JSON.stringify(memory));
      GarageStore.broadcast();
      return memory;
    }

    static clear() {
      memory = null;
      drop(GarageStore.key);
      GarageStore.broadcast();
      return null;
    }

    /* Any element may carry the key, so upgrade order does not matter. */
    static configure(key) {
      if (!key || key === GarageStore.key) return;
      GarageStore.key = key;
      memory = null;
      GarageStore.broadcast();
    }

    static broadcast() {
      const value = GarageStore.get();
      document.dispatchEvent(
        new CustomEvent(EVENT, {
          detail: { slug: value ? value.slug : '', name: value ? value.name : '' }
        })
      );
    }

    connectedCallback() {
      GarageStore.configure(this.getAttribute('key'));
    }
  }

  GarageStore.key = DEFAULT_KEY;

  function currentModel() {
    return GarageStore.get() || { slug: '', name: '' };
  }

  function listen(element, render) {
    GarageStore.configure(element.getAttribute('key'));
    element[HANDLER] = function (event) {
      render.call(element, event.detail || { slug: '', name: '' });
    };
    document.addEventListener(EVENT, element[HANDLER]);
    render.call(element, currentModel());
  }

  function forget(element) {
    if (element[HANDLER]) document.removeEventListener(EVENT, element[HANDLER]);
    element[HANDLER] = null;
  }

  class FitmentFinder extends HTMLElement {
    connectedCallback() {
      this.chips = Array.from(this.querySelectorAll('[data-garage-chip]'));
      this.cta = this.querySelector('[data-garage-cta]');
      this.ctaDefault = this.cta ? this.cta.textContent.trim() : '';
      this.live = this.querySelector('[data-garage-live]');
      this.liveText = this.querySelector('[data-garage-live-text]');
      this.touched = false;
      this.onClick = this.handleClick.bind(this);
      this.addEventListener('click', this.onClick);
      listen(this, this.render);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      forget(this);
    }

    handleClick(event) {
      const chip = event.target.closest('[data-garage-chip]');
      if (!chip || !this.contains(chip)) return;
      const slug = chip.getAttribute('data-slug');
      if (!slug) return;
      this.touched = true;
      /* The active chip empties the garage: the way back to "show everything". */
      if (chip.getAttribute('aria-pressed') === 'true') {
        GarageStore.clear();
        return;
      }
      GarageStore.set(slug, chip.getAttribute('data-name') || chip.textContent.trim());
    }

    /* Counted in the CTA's own target block, so the number matches what the visitor will
       scroll to; nested data-fits is skipped. 0 = "cannot say": the CTA drops the number
       instead of inventing one. */
    countParts(slug) {
      const selector = this.getAttribute('data-target');
      let scope = null;
      if (selector) {
        try {
          scope = document.querySelector(selector);
        } catch (error) {
          scope = null;
        }
      }
      if (!scope) return 0;
      let total = 0;
      for (const node of scope.querySelectorAll('[data-fits]')) {
        if (node.parentElement && node.parentElement.closest('[data-fits]')) continue;
        if (fitsModel(node.getAttribute('data-fits'), slug)) total++;
      }
      return total;
    }

    render(detail) {
      const slug = detail.slug;
      let name = detail.name;

      for (const chip of this.chips) {
        const active = Boolean(slug) && chip.getAttribute('data-slug') === slug;
        chip.setAttribute('aria-pressed', active ? 'true' : 'false');
        if (active && !name) name = chip.getAttribute('data-name') || chip.textContent.trim();
      }

      if (this.cta) {
        if (!slug) {
          setText(this.cta, this.ctaDefault);
        } else {
          const count = this.countParts(slug);
          let template = this.getAttribute('data-cta-nocount');
          if (count > 0) {
            template =
              this.getAttribute('data-cta-' + pluralForm(count)) ||
              this.getAttribute('data-cta-other');
          }
          setText(this.cta, fill(template, count, name));
        }
      }

      if (this.live) {
        /* Unhide before writing: AT announces changes inside a visible live region. */
        if (slug || this.touched) {
          this.live.hidden = false;
          if (slug) setText(this.liveText, fill(this.getAttribute('data-saved'), '', name));
          else setText(this.liveText, this.getAttribute('data-cleared'));
        } else {
          this.live.hidden = true;
        }
      }
    }
  }

  class GarageFilter extends HTMLElement {
    connectedCallback() {
      this.empty = this.querySelector('[data-garage-empty]');
      this.emptyText = this.querySelector('[data-garage-empty-text]');
      listen(this, this.render);
    }

    disconnectedCallback() {
      forget(this);
    }

    render(detail) {
      const slug = detail.slug;
      const hide = this.getAttribute('mode') === 'hide';
      const nodes = this.querySelectorAll('[data-fits]');
      let shown = 0;

      for (const node of nodes) {
        if (!slug) {
          node.removeAttribute('data-fit-state');
          if (hide) node.hidden = false;
          continue;
        }
        const match = fitsModel(node.getAttribute('data-fits'), slug);
        node.setAttribute('data-fit-state', match ? 'fit' : 'nofit');
        if (hide) node.hidden = !match;
        if (match) shown++;
      }

      this.setAttribute('data-garage-state', slug ? 'filtered' : 'all');

      if (this.empty) {
        const nothing = Boolean(slug) && hide && nodes.length > 0 && shown === 0;
        if (nothing) setText(this.emptyText, fill(this.getAttribute('data-empty'), '', detail.name));
        this.empty.hidden = !nothing;
      }
    }
  }

  class FitBadge extends HTMLElement {
    connectedCallback() {
      this.textEl = this.querySelector('[data-fit-text]');
      /* Server text lists the models this part fits: honest while nothing is picked. */
      this.unselectedText = this.textEl ? this.textEl.textContent : '';
      listen(this, this.render);
    }

    disconnectedCallback() {
      forget(this);
    }

    render(detail) {
      const host = this.closest('[data-fits]');
      const value = host ? host.getAttribute('data-fits') : '';

      if (!detail.slug || !value) {
        this.setAttribute('data-fit-state', 'unknown');
        setText(this.textEl, this.unselectedText);
        return;
      }

      const match = fitsModel(value, detail.slug);
      this.setAttribute('data-fit-state', match ? 'fit' : 'nofit');
      const template = this.getAttribute(match ? 'data-label-fits' : 'data-label-not');
      setText(this.textEl, fill(template, '', detail.name));
    }
  }

  function define(name, ctor) {
    if (!window.customElements.get(name)) window.customElements.define(name, ctor);
  }

  define('garage-store', GarageStore);
  define('fitment-finder', FitmentFinder);
  define('garage-filter', GarageFilter);
  define('fit-badge', FitBadge);
})();
